// Standard maintenance schedules by category
// Intervals are in miles and months
// powertrain: 'ice' = ICE only, 'ev' = EV only, 'all' = both (default)

export const MAINTENANCE_ITEMS = {
  // Engine & Fluids (ICE only)
  oil_change: {
    name: 'Oil Change',
    category: 'Engine & Fluids',
    icon: 'Droplet',
    defaultMiles: 5000,
    defaultMonths: 6,
    severity: 'high',
    description: 'Engine oil and filter replacement',
    tips: 'Use manufacturer-recommended oil weight. Synthetic oils may allow longer intervals.',
    powertrain: 'ice',
  },
  coolant_flush: {
    name: 'Coolant Flush',
    category: 'Engine & Fluids',
    icon: 'Thermometer',
    defaultMiles: 30000,
    defaultMonths: 24,
    severity: 'medium',
    description: 'Replace engine coolant/antifreeze',
    tips: 'Check coolant level monthly. Use correct coolant type for your vehicle.',
    powertrain: 'ice',
  },
  transmission_fluid: {
    name: 'Transmission Fluid',
    category: 'Engine & Fluids',
    icon: 'Cog',
    defaultMiles: 60000,
    defaultMonths: 48,
    severity: 'medium',
    description: 'Transmission fluid change or flush',
    tips: 'Manual transmissions may have different intervals. Check for leaks regularly.',
    powertrain: 'ice',
  },
  brake_fluid: {
    name: 'Brake Fluid Flush',
    category: 'Brakes & Safety',
    icon: 'AlertCircle',
    defaultMiles: 30000,
    defaultMonths: 24,
    severity: 'high',
    description: 'Replace brake fluid',
    tips: 'Brake fluid absorbs moisture over time, reducing effectiveness.',
    powertrain: 'all',
  },
  power_steering_fluid: {
    name: 'Power Steering Fluid',
    category: 'Engine & Fluids',
    icon: 'RotateCw',
    defaultMiles: 50000,
    defaultMonths: 36,
    severity: 'low',
    description: 'Check/replace power steering fluid',
    tips: 'Listen for whining sounds when turning - may indicate low fluid.',
    powertrain: 'ice',
  },
  differential_fluid: {
    name: 'Differential Fluid',
    category: 'Engine & Fluids',
    icon: 'Circle',
    defaultMiles: 50000,
    defaultMonths: 48,
    severity: 'low',
    description: 'Replace differential/axle fluid',
    tips: 'AWD and 4WD vehicles may need more frequent changes.',
    powertrain: 'ice',
  },

  // Brakes & Safety (all vehicles)
  brake_pads: {
    name: 'Brake Pads',
    category: 'Brakes & Safety',
    icon: 'Disc',
    defaultMiles: 40000,
    defaultMonths: 36,
    severity: 'high',
    description: 'Inspect/replace brake pads',
    tips: 'Driving style greatly affects pad life. Listen for squealing sounds.',
    powertrain: 'all',
  },
  brake_rotors: {
    name: 'Brake Rotors',
    category: 'Brakes & Safety',
    icon: 'Disc',
    defaultMiles: 70000,
    defaultMonths: 60,
    severity: 'high',
    description: 'Inspect/resurface/replace brake rotors',
    tips: 'Rotors can often be resurfaced once before replacement.',
    powertrain: 'all',
  },

  // Tires & Wheels (all vehicles)
  tire_rotation: {
    name: 'Tire Rotation',
    category: 'Tires & Wheels',
    icon: 'CircleDot',
    defaultMiles: 7500,
    defaultMonths: 6,
    severity: 'medium',
    description: 'Rotate tires for even wear',
    tips: 'Check tire pressure monthly. EVs are heavier and may wear tires faster.',
    powertrain: 'all',
  },
  wheel_alignment: {
    name: 'Wheel Alignment',
    category: 'Tires & Wheels',
    icon: 'Move',
    defaultMiles: 25000,
    defaultMonths: 24,
    severity: 'medium',
    description: 'Check and adjust wheel alignment',
    tips: 'Get alignment after hitting potholes or curbs. Uneven tire wear is a sign.',
    powertrain: 'all',
  },
  tire_replacement: {
    name: 'Tire Replacement',
    category: 'Tires & Wheels',
    icon: 'Circle',
    defaultMiles: 50000,
    defaultMonths: 60,
    severity: 'high',
    description: 'Replace worn tires',
    tips: 'Check tread depth with penny test. Replace when tread is at 2/32".',
    powertrain: 'all',
  },

  // Filters
  air_filter: {
    name: 'Engine Air Filter',
    category: 'Filters',
    icon: 'Wind',
    defaultMiles: 20000,
    defaultMonths: 24,
    severity: 'low',
    description: 'Replace engine air filter',
    tips: 'Dusty conditions require more frequent replacement.',
    powertrain: 'ice',
  },
  cabin_air_filter: {
    name: 'Cabin Air Filter',
    category: 'Filters',
    icon: 'Wind',
    defaultMiles: 20000,
    defaultMonths: 12,
    severity: 'low',
    description: 'Replace cabin/HVAC air filter',
    tips: 'Replace if you notice musty smells from AC vents.',
    powertrain: 'all',
  },
  fuel_filter: {
    name: 'Fuel Filter',
    category: 'Filters',
    icon: 'Filter',
    defaultMiles: 40000,
    defaultMonths: 36,
    severity: 'medium',
    description: 'Replace fuel filter',
    tips: 'Some modern vehicles have lifetime filters. Check your manual.',
    powertrain: 'ice',
  },

  // Belts & Timing (ICE only)
  serpentine_belt: {
    name: 'Serpentine Belt',
    category: 'Belts & Timing',
    icon: 'Repeat',
    defaultMiles: 60000,
    defaultMonths: 60,
    severity: 'medium',
    description: 'Inspect/replace serpentine belt',
    tips: 'Look for cracks, fraying, or glazing. Belt failure can strand you.',
    powertrain: 'ice',
  },
  timing_belt: {
    name: 'Timing Belt',
    category: 'Belts & Timing',
    icon: 'Clock',
    defaultMiles: 90000,
    defaultMonths: 84,
    severity: 'high',
    description: 'Replace timing belt (if equipped)',
    tips: 'Critical maintenance! Failure can cause major engine damage on interference engines.',
    powertrain: 'ice',
  },
  timing_chain: {
    name: 'Timing Chain Inspection',
    category: 'Belts & Timing',
    icon: 'Link',
    defaultMiles: 150000,
    defaultMonths: 120,
    severity: 'medium',
    description: 'Inspect timing chain for stretch/wear',
    tips: 'Chains typically last longer than belts but should still be inspected.',
    powertrain: 'ice',
  },

  // Ignition & Electrical
  spark_plugs: {
    name: 'Spark Plugs',
    category: 'Ignition & Electrical',
    icon: 'Zap',
    defaultMiles: 60000,
    defaultMonths: 60,
    severity: 'medium',
    description: 'Replace spark plugs',
    tips: 'Iridium/platinum plugs last longer. Replace wires if equipped.',
    powertrain: 'ice',
  },
  battery: {
    name: '12V Battery',
    category: 'Electrical',
    icon: 'Battery',
    defaultMiles: 50000,
    defaultMonths: 48,
    severity: 'medium',
    description: 'Test/replace 12V accessory battery',
    tips: 'Have battery tested annually after 3 years. Keep terminals clean.',
    powertrain: 'all',
  },

  // Suspension & Steering (all vehicles)
  shocks_struts: {
    name: 'Shocks & Struts',
    category: 'Suspension',
    icon: 'ArrowUpDown',
    defaultMiles: 75000,
    defaultMonths: 72,
    severity: 'medium',
    description: 'Inspect/replace shocks and struts',
    tips: 'Bouncy ride or uneven tire wear indicates worn suspension.',
    powertrain: 'all',
  },

  // EV-Specific Maintenance
  battery_health_check: {
    name: 'Battery Health Check',
    category: 'EV Powertrain',
    icon: 'BatteryFull',
    defaultMiles: 15000,
    defaultMonths: 12,
    severity: 'high',
    description: 'Check high-voltage battery state of health and degradation',
    tips: 'Monitor range trends. Avoid keeping battery at 100% for extended periods.',
    powertrain: 'ev',
  },
  ev_coolant: {
    name: 'Battery Coolant',
    category: 'EV Powertrain',
    icon: 'Thermometer',
    defaultMiles: 50000,
    defaultMonths: 48,
    severity: 'medium',
    description: 'Replace battery thermal management coolant',
    tips: 'Battery cooling systems are critical for longevity and performance.',
    powertrain: 'ev',
  },
  ev_motor_inspection: {
    name: 'Motor & Inverter Inspection',
    category: 'EV Powertrain',
    icon: 'Cog',
    defaultMiles: 75000,
    defaultMonths: 60,
    severity: 'medium',
    description: 'Inspect electric motor, inverter, and reduction gear',
    tips: 'Listen for unusual sounds. EV drivetrains are generally very reliable.',
    powertrain: 'ev',
  },
  ev_charging_system: {
    name: 'Charging System Check',
    category: 'EV Powertrain',
    icon: 'Plug',
    defaultMiles: null,
    defaultMonths: 24,
    severity: 'medium',
    description: 'Inspect charging port, cables, and onboard charger',
    tips: 'Keep charging port clean and dry. Check cable insulation for wear.',
    powertrain: 'ev',
  },
  ev_regen_brakes: {
    name: 'Regenerative Braking Check',
    category: 'Brakes & Safety',
    icon: 'RefreshCw',
    defaultMiles: 30000,
    defaultMonths: 24,
    severity: 'low',
    description: 'Check regenerative braking calibration and performance',
    tips: 'Regen braking reduces brake pad wear significantly on EVs.',
    powertrain: 'ev',
  },

  // Collector Car Specific (all vehicles)
  storage_prep: {
    name: 'Winter Storage Prep',
    category: 'Collector Care',
    icon: 'Snowflake',
    defaultMiles: null,
    defaultMonths: 12,
    severity: 'medium',
    description: 'Prepare vehicle for seasonal storage',
    tips: 'Fuel stabilizer, battery tender, proper cover, clean interior.',
    powertrain: 'all',
  },
  detail_service: {
    name: 'Full Detail',
    category: 'Collector Care',
    icon: 'Sparkles',
    defaultMiles: null,
    defaultMonths: 6,
    severity: 'low',
    description: 'Professional detail and paint protection',
    tips: 'Regular detailing preserves value and protects finishes.',
    powertrain: 'all',
  },
  leather_conditioning: {
    name: 'Leather Conditioning',
    category: 'Collector Care',
    icon: 'Armchair',
    defaultMiles: null,
    defaultMonths: 3,
    severity: 'low',
    description: 'Clean and condition leather surfaces',
    tips: 'Prevents cracking and fading. Use quality leather conditioner.',
    powertrain: 'all',
  },
}

// Vehicle type presets - which maintenance items apply
export const VEHICLE_PRESETS = {
  modern_daily: {
    name: 'Modern Daily Driver',
    description: 'Post-2010 vehicle for regular use',
    items: [
      'oil_change', 'tire_rotation', 'brake_pads', 'brake_fluid',
      'air_filter', 'cabin_air_filter', 'spark_plugs', 'battery',
      'coolant_flush', 'transmission_fluid', 'wheel_alignment',
      'serpentine_belt', 'tire_replacement'
    ]
  },
  classic_car: {
    name: 'Classic/Vintage (Pre-1985)',
    description: 'Collector vehicles requiring special care',
    items: [
      'oil_change', 'brake_pads', 'brake_fluid', 'brake_rotors',
      'tire_rotation', 'air_filter', 'spark_plugs', 'battery',
      'coolant_flush', 'transmission_fluid', 'differential_fluid',
      'storage_prep', 'detail_service', 'leather_conditioning',
      'fuel_filter', 'wheel_alignment'
    ],
    intervalMultiplier: 0.5 // More frequent by time for low-mileage classics
  },
  sports_car: {
    name: 'Sports/Performance Car',
    description: 'High-performance vehicles',
    items: [
      'oil_change', 'brake_pads', 'brake_fluid', 'brake_rotors',
      'tire_rotation', 'air_filter', 'cabin_air_filter', 'spark_plugs',
      'coolant_flush', 'transmission_fluid', 'differential_fluid',
      'wheel_alignment', 'tire_replacement', 'shocks_struts',
      'detail_service'
    ],
    intervalMultiplier: 0.75 // Slightly more frequent
  },
  suv_truck: {
    name: 'SUV/Truck',
    description: 'Larger vehicles, may include 4WD',
    items: [
      'oil_change', 'tire_rotation', 'brake_pads', 'brake_fluid',
      'air_filter', 'cabin_air_filter', 'spark_plugs', 'battery',
      'coolant_flush', 'transmission_fluid', 'differential_fluid',
      'wheel_alignment', 'serpentine_belt', 'tire_replacement',
      'shocks_struts', 'fuel_filter'
    ]
  },
  exotic: {
    name: 'Exotic/Supercar',
    description: 'Ferrari, Lamborghini, etc.',
    items: [
      'oil_change', 'brake_pads', 'brake_fluid', 'brake_rotors',
      'tire_rotation', 'air_filter', 'cabin_air_filter', 'spark_plugs',
      'coolant_flush', 'transmission_fluid', 'timing_belt',
      'serpentine_belt', 'wheel_alignment', 'tire_replacement',
      'detail_service', 'leather_conditioning', 'storage_prep'
    ],
    intervalMultiplier: 0.5 // More frequent service for exotics
  },
  electric: {
    name: 'Electric Vehicle',
    description: 'Battery electric vehicles (Tesla, Rivian, Porsche Taycan, etc.)',
    items: [
      'battery_health_check', 'ev_coolant', 'ev_motor_inspection',
      'ev_charging_system', 'ev_regen_brakes',
      'brake_pads', 'brake_fluid', 'brake_rotors',
      'tire_rotation', 'wheel_alignment', 'tire_replacement',
      'cabin_air_filter', 'battery', 'shocks_struts',
      'detail_service', 'leather_conditioning'
    ],
    intervalMultiplier: 1.0
  }
}

// Helper: check if a fuel_type string represents an EV
export function isElectricVehicle(fuelType) {
  if (!fuelType) return false
  const ft = fuelType.toLowerCase()
  return ft.includes('electric') && !ft.includes('hybrid')
}

// Helper: check if a fuel_type string represents a hybrid
export function isHybridVehicle(fuelType) {
  if (!fuelType) return false
  const ft = fuelType.toLowerCase()
  return ft.includes('hybrid') || ft.includes('plug-in')
}

// Get maintenance items filtered by powertrain type
export function getMaintenanceItemsForPowertrain(fuelType) {
  const isEV = isElectricVehicle(fuelType)
  const isHybrid = isHybridVehicle(fuelType)

  return Object.fromEntries(
    Object.entries(MAINTENANCE_ITEMS).filter(([_, item]) => {
      if (!item.powertrain || item.powertrain === 'all') return true
      if (isEV) return item.powertrain === 'ev'
      if (isHybrid) return true // hybrids need both ICE and EV items
      return item.powertrain === 'ice'
    })
  )
}

// Calculate when maintenance is due
export function calculateMaintenanceDue(item, lastServiceDate, lastServiceMiles, currentMiles) {
  const schedule = MAINTENANCE_ITEMS[item]
  if (!schedule) return null

  const now = new Date()
  let dueMiles = null
  let dueDate = null
  let urgency = 'ok' // ok, soon, due, overdue

  // Calculate miles-based due date
  if (schedule.defaultMiles && lastServiceMiles !== null) {
    dueMiles = lastServiceMiles + schedule.defaultMiles
    const milesRemaining = dueMiles - currentMiles

    if (milesRemaining < 0) {
      urgency = 'overdue'
    } else if (milesRemaining < schedule.defaultMiles * 0.1) {
      urgency = 'due'
    } else if (milesRemaining < schedule.defaultMiles * 0.25) {
      urgency = 'soon'
    }
  }

  // Calculate time-based due date
  if (schedule.defaultMonths && lastServiceDate) {
    const lastDate = new Date(lastServiceDate)
    dueDate = new Date(lastDate)
    dueDate.setMonth(dueDate.getMonth() + schedule.defaultMonths)

    const daysRemaining = Math.floor((dueDate - now) / (1000 * 60 * 60 * 24))

    if (daysRemaining < 0 && urgency !== 'overdue') {
      urgency = 'overdue'
    } else if (daysRemaining < 30 && urgency !== 'overdue') {
      urgency = urgency === 'due' ? 'due' : 'due'
    } else if (daysRemaining < 60 && urgency === 'ok') {
      urgency = 'soon'
    }
  }

  // For time-only items (like storage prep)
  if (!schedule.defaultMiles && schedule.defaultMonths && lastServiceDate) {
    const lastDate = new Date(lastServiceDate)
    dueDate = new Date(lastDate)
    dueDate.setMonth(dueDate.getMonth() + schedule.defaultMonths)

    const daysRemaining = Math.floor((dueDate - now) / (1000 * 60 * 60 * 24))

    if (daysRemaining < 0) urgency = 'overdue'
    else if (daysRemaining < 30) urgency = 'due'
    else if (daysRemaining < 60) urgency = 'soon'
  }

  return {
    item,
    ...schedule,
    dueMiles,
    dueDate,
    urgency,
    milesRemaining: dueMiles ? dueMiles - currentMiles : null,
    daysRemaining: dueDate ? Math.floor((dueDate - now) / (1000 * 60 * 60 * 24)) : null
  }
}

// Get all upcoming maintenance for a vehicle
export function getUpcomingMaintenance(vehicleType, serviceHistory, currentMiles) {
  const preset = VEHICLE_PRESETS[vehicleType] || VEHICLE_PRESETS.modern_daily
  const results = []

  for (const itemKey of preset.items) {
    // Find last service for this item
    const lastService = serviceHistory?.find(s =>
      s.maintenance_type === itemKey ||
      s.description?.toLowerCase().includes(MAINTENANCE_ITEMS[itemKey]?.name.toLowerCase())
    )

    const result = calculateMaintenanceDue(
      itemKey,
      lastService?.date || null,
      lastService?.mileage || null,
      currentMiles
    )

    if (result) {
      // Apply interval multiplier for vehicle type
      if (preset.intervalMultiplier && result.milesRemaining) {
        result.adjustedMilesRemaining = result.milesRemaining * preset.intervalMultiplier
      }
      results.push(result)
    }
  }

  // Sort by urgency then by due date/miles
  const urgencyOrder = { overdue: 0, due: 1, soon: 2, ok: 3 }
  return results.sort((a, b) => {
    if (urgencyOrder[a.urgency] !== urgencyOrder[b.urgency]) {
      return urgencyOrder[a.urgency] - urgencyOrder[b.urgency]
    }
    // Secondary sort by days remaining
    return (a.daysRemaining || 999) - (b.daysRemaining || 999)
  })
}
